import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import axios from 'axios';
import styled from 'styled-components';

import ChatHeader from './Header';
import { EnrichedMessage, ManagedSession } from '../api/managed/ManagedSession';
import {
    manager,
    SessionType,
    StartProxySessionProperties,
    StartSessionProperties,
} from '../api/SessionManager';
import { useManagedSession, useManagedStatus } from '../hooks/useSessionManager';
import { useMessages } from '../hooks/useMessages';
import { translated } from '../utils/TranslationUtil';
import { SessionContextValue, SessionProvider } from '../utils/SessionContext';
import { IntentFeedback } from '../types/enums';

import {
    Alert,
    Body,
    Bubble,
    Entry,
    Event,
    FeedbackCard,
    Footer,
    Select,
    TypingIndicator,
    WelcomeScreen,
} from '../components';

import {
    ContentTypeEnumSchema,
    JsonContentTypeEnumSchema,
    MessageParticipantRole,
    MessageType,
    ResponseMessage,
} from '@amzn/ring-cs-chatbot-service-model';
import { apiPrefix } from '../settings';
import {
    sessionStarter,
    StartPublicSessionHandler,
    StartSessionHandler,
    ConnectionDetails,
    ManagedStatus,
    SessionComponent,
} from '@amzn/ring-cs-chatbot-widget';

type Message = ResponseMessage & {
    content: string;
};

type LiveSessionProps = {
    widgetOpen: boolean;
    windowType: 'mobile' | 'tablet' | 'desktop';
    onNewMessage: () => void;
    onPlayAudio: () => void;
    onToggleWidget: () => void;
    session: ManagedSession;
};

export const LiveSessionWrapper: SessionComponent = ({
    widgetOpen,
    windowType,
    onNewMessage,
    onPlayAudio,
    onToggleWidget,
    onStatusHandler,
    onClickStart,
}) => {
    const session = useManagedSession();
    const status = useManagedStatus();

    const [startError, setStartError] = useState(false);
    const [startLoading, setStartLoading] = useState(false);

    useEffect(() => {
        const startPublicSession: StartPublicSessionHandler = async (unauthAccessDetails) => {
            const getConnectionDetails = async (): Promise<ConnectionDetails> => {
                try {
                    const endpoint = `${apiPrefix}/prospect/unauthedAccess`;
                    const payload = {
                        ...unauthAccessDetails
                    };

                    const response = await axios.post<ConnectionDetails>(endpoint, payload);
                    const {
                        identityId,
                        token,
                        sessionId,
                        attributes,
                    } = response.data;

                    return {
                        identityId,
                        token,
                        sessionId,
                        attributes,
                    };
                } catch (e) {
                    console.error('Error calling unauthedAccess:', e);
                    throw e;
                }
            };

            return await startSession(getConnectionDetails());
        };

        const startSession: StartSessionHandler = async (connectionDetails) => {
            const properties: Promise<StartSessionProperties> = Promise.resolve(connectionDetails)
                .then(details => {
                    if (details.identityId && details.token) {
                        const proxyProperties: StartProxySessionProperties = {
                            type: SessionType.PROXY,
                            identityId: details.identityId,
                            token: details.token,
                            sessionId: details.sessionId,
                            attributes: details.attributes || {}
                        };

                        return proxyProperties;
                    } else {
                        throw new Error('Missing required proxy properties');
                    }
                });

            // ensure all await'ing is inside start session so status is reflected
            await manager.startSession(properties);

            // successfully started if no error thrown
            return true;
        };

        const subscriptions = [
            sessionStarter.onStartPublicSession(startPublicSession),
            sessionStarter.onStartSession(startSession),
        ];

        return () => subscriptions.forEach(unsubscribe => unsubscribe());
    }, []);

    useEffect(() => {
        if (onStatusHandler) {
            onStatusHandler(status);
        }
    }, [status, onStatusHandler]);

    const handleStartChat = useCallback(async () => {
        setStartLoading(true);
        try {
            if (onClickStart) {
                await onClickStart();
            }
        } catch (err) {
            console.error('Failed to start chat:', err);
            setStartError(true);
        } finally {
            setStartLoading(false);
        }
    }, [onClickStart]);

    if (!session) {
        return (
            <WelcomeScreen
                ariaLabel={translated('Components.WelcomeScreen.ariaLabel')}
                title={translated('Components.WelcomeScreen.title')}
                subtitleHtml={translated('Components.WelcomeScreen.subtitleHtml')}
                buttonText={translated('Components.WelcomeScreen.button')}
                startHandler={handleStartChat}
                toggleOpen={onToggleWidget}
                loading={startLoading || status === ManagedStatus.STARTING}
                error={startError ? translated('Components.WelcomeScreen.error') : ''}
                messageLabel={translated('Components.WelcomeScreen.messageLabel')}
            >
                {status === ManagedStatus.ENDED && (
                    <Alert
                        ariaLabel={translated('Components.Alert.ariaLabel')}
                        color={'primary'}
                        message={translated('Components.Alert.sessionEnded')}
                        position={'topBody'}
                    />
                )}
            </WelcomeScreen>
        )
    }

    return (
        <LiveSession
            widgetOpen={widgetOpen}
            windowType={windowType}
            onNewMessage={onNewMessage}
            onPlayAudio={onPlayAudio}
            onToggleWidget={onToggleWidget}
            session={session}
        />
    )
};

/*
 * To ensure that all chat components share the same stacking context within the SessionContainer
 * and to resolve an iOS 17.x issue where the Body can appear above the ChatHeader menu,
 * we pass the SessionContainer's DOM element (via a ref) to the Header component.
 *
 * The Header component then uses this as the portal container for rendering the MoreMenu.
 * This guarantees that the MoreMenu is rendered within the SessionContainer's stacking context,
 * preventing any unexpected layering issues on iOS.
 *
 * If no portal element is provided, the MoreMenu will render in place.
 */
const SessionContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    height: 100%;
`;

const LiveSession: React.FC<LiveSessionProps> = ({
    widgetOpen,
    windowType,
    onNewMessage,
    onPlayAudio,
    onToggleWidget,
    session,
}) => {
    const {
        currentParticipantRoles,
        groupMessagesByRole,
        initialLoading,
        loading,
        loadPreviousMessages,
        messageError,
        sendingCustomerMessage,
        pendingMessage,
        selectedOptions,
        sendFeedback,
        sendMessage,
        sendSurvey,
        setLoading,
        typingRole,
    } = useMessages(session);

    const sessionContainer = useRef<HTMLDivElement>(null);

    const {
        latestReadMessageId,
        latestDeliveredMessageId,
    } = session.useReceipts(widgetOpen);

    const [endChatError, setEndChatError] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [surveyRequested, setSurveyRequested] = useState(false);

    const toggleMute = useCallback(() => {
        setIsMuted((prev) => !prev);
    }, []);

    // Callback for subscribing to new messages (show new message indicator in Body)
    const subscribeToNewMessage = useCallback((handler: () => void) => {
        return session.onMessage({
            handler: (message: ResponseMessage) => {
                if (message.type === MessageType.MESSAGE && message.participantRole !== MessageParticipantRole.CUSTOMER) {
                    handler();
                }
            },
        });
    }, [session])

    // retrieve session context fields
    const sessionContext: SessionContextValue = useMemo(() => {
        const attributes = session.getAttributes();
        return {
            sessionId: session.getSessionId(),
            enableSurvey: attributes.enableSurvey === 'true',
            enableBot: attributes.enableSherlock === 'true' || !!attributes.enableAgents,
        };
    }, [session]);

    // Play chat notification tone & show NotificationCard when Widget is hidden
    useEffect(() => {
        const unsubscribe = session.onMessage({
            handler: ({ type, contentType, participantRole }) => {
                const isAgentMessage =
                    type === MessageType.MESSAGE &&
                    participantRole !== MessageParticipantRole.CUSTOMER;
                const isIdleEvent =
                    type === MessageType.EVENT &&
                    contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.idle'];

                if (!isMuted && (isAgentMessage || isIdleEvent)) onPlayAudio();
                if (isAgentMessage) onNewMessage();
            }
        });
        return () => unsubscribe();
    }, [session, onNewMessage, isMuted, onPlayAudio]);

    const endChat = useCallback(async () => {
        setLoading(true);
        try {
            await session.end();
        } catch (error) {
            console.error('Failed to end chat session:', error);
            setEndChatError(true);
        } finally {
            setLoading(false);
        }
    }, [session, setLoading]);

    const allMessages = session.useMessages();

    const feedbackMap = useMemo(() => {
        type Feedback = { rating: IntentFeedback, reason?: string };
        const map: Record<string, Feedback> = {};
        allMessages.forEach(msg => {
            if (
                msg.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/sherlock.event.feedback.thumbs.up'] ||
                msg.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/sherlock.event.feedback.thumbs.down']
            ) {
                const requestMessageId = msg.jsonContent.attributes?.requestMessageId;
                if (requestMessageId) {
                    if (msg.jsonContent.type === JsonContentTypeEnumSchema.enum['application/sherlock.event.feedback.thumbs.up']) {
                        map[requestMessageId] = { rating: IntentFeedback.THUMBS_UP };
                    } else {
                        // For thumbs down, include an optional reason if it exists.
                        map[requestMessageId] = {
                            rating: IntentFeedback.THUMBS_DOWN,
                            reason: msg.jsonContent.attributes?.reason
                        };
                    }
                }
            }
        });
        return map;
    }, [allMessages]);

    /* Note:
    * Messages at the end of the chat are actionable, up to (and including) the
    * most recent message from flows or custom bedrock response. This is
    * because contact flows and custom bot have state, and they can only accept
    * responses for their most recent query.
    */
    const actionableMessages = useMemo(() => {
        const actionableMessages = new Set<string>();

        // reverse iterate through all messages to find actionable messages
        for (let i = allMessages.length - 1; i >= 0; i--) {
            const message = allMessages[i];
            actionableMessages.add(message.messageId);

            // these messages CANNOT have actionable messages before them
            const contentTypes: string[] = [
                ContentTypeEnumSchema.enum['text/plain'],
            ];
            const jsonContentTypes: string[] = [
                JsonContentTypeEnumSchema.enum['application/sherlock.message.bedrock.response'],
                JsonContentTypeEnumSchema.enum['application/chat.message.sherlock.response'],
                JsonContentTypeEnumSchema.enum['application/chat.message.vqa.response'],
                JsonContentTypeEnumSchema.enum['application/chat.message.sherpa.response'],
            ];
            if (contentTypes.includes(message.contentType) ||
                (message.jsonContent?.type && jsonContentTypes.includes(message.jsonContent.type))) {
                break;
            }
        }

        return actionableMessages;
    }, [allMessages]);

    /**
     * Determines visibility of survey option in More Menu.
     * Shows survey when:
     * - Survey is enabled (enableSurvey check)
     * - Survey has not been requested yet
     * - Either an agent or custom bot is present in the conversation
     */
    const showSurvey = useMemo(() => {
        return (
            sessionContext.enableSurvey &&
            !surveyRequested &&
            (currentParticipantRoles.has(MessageParticipantRole.AGENT) ||
                currentParticipantRoles.has(MessageParticipantRole.CUSTOM_BOT))
        );
    }, [
        sessionContext,
        surveyRequested,
        currentParticipantRoles,
    ]);

    const handleSendSurvey = useCallback(() => {
        sendSurvey();
        setSurveyRequested(true);
    }, [sendSurvey]);

    const componentConversion = (message: Message) => {
        const isActionable = actionableMessages.has(message.messageId);
        const selectedOption = selectedOptions[message.messageId];
        const receipt = message.messageId === latestReadMessageId
            ? 'READ'
            : message.messageId === latestDeliveredMessageId
                ? 'DELIVERED'
                : undefined;

        let baseComponent: JSX.Element;

        switch (message.contentType) {
            case ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.joined']:
                {
                    // Display Joined Events for agent only
                    if (message.participantRole === MessageParticipantRole.AGENT) {
                        baseComponent = (
                            <Event
                                key={message.messageId}
                                indicator={message.participantRole}
                                icon='user'
                                text={`${message.displayName} ${translated('Components.Event.agentJoin')}`}
                            />
                        );
                    } else {
                        baseComponent = (<></>);
                    };
                }
                break;
            case ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.left']:
                {
                    // Display Left Events for agent only
                    if (message.participantRole === MessageParticipantRole.AGENT) {
                        baseComponent = (
                            <Event
                                key={message.messageId}
                                indicator={message.participantRole}
                                icon='user'
                                text={`${message.displayName} ${translated('Components.Event.agentLeft')}`}
                            />
                        );
                    } else {
                        baseComponent = (<></>);
                    };
                }
                break;
            case ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.idle']:
                {
                    const idleText = translated('Components.Event.idle');
                    baseComponent = (
                        <>
                            <Event key={`${message.messageId}-idle`} indicator='IDLE' icon='timer' text={idleText} />
                            <Select
                                feedback={true}
                                disabled={!isActionable}
                                items={[
                                    {
                                        label: translated('Components.Event.idleContinue'),
                                        value: translated('Components.Event.idleContinue'),
                                        onClick: (value) => {
                                            sendMessage(value);
                                        },
                                    },
                                    {
                                        label: translated('Components.Event.idleEnd'),
                                        value: translated('Components.Event.idleEnd'),
                                        onClick: endChat,
                                    },
                                ]}
                            />
                        </>
                    );
                }
                break;
            case ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.returned']:
                {
                    const returnedText = translated('Components.Event.returned');
                    baseComponent = (
                        <Event
                            key={message.messageId}
                            indicator={message.participantRole}
                            icon='user'
                            text={returnedText}
                        />
                    );
                }
                break;
            default:
                baseComponent = (
                    <Bubble
                        ariaLabel={translated('Components.Bubble.ariaLabel')}
                        key={message.messageId}
                        customer={message.participantRole === MessageParticipantRole.CUSTOMER}
                        isActionable={isActionable}
                        selectedOption={selectedOption}
                        receipt={receipt}
                        sendMessage={sendMessage}
                        content={message.content}
                    />
                );
        }

        // Handle Enriched Messages (Custom Bot Responses & Feedback Requests)
        const enrichedMessage = message as EnrichedMessage;
        switch (enrichedMessage.jsonContent?.type) {
            case JsonContentTypeEnumSchema.enum['application/sherlock.message.bedrock.response']:
            case JsonContentTypeEnumSchema.enum['application/chat.message.sherlock.response']:
                baseComponent = (
                    <Bubble
                        ariaLabel={translated('Components.Bubble.ariaLabel')}
                        key={message.messageId}
                        customer={message.participantRole === MessageParticipantRole.CUSTOMER}
                        isActionable={isActionable}
                        selectedOption={selectedOption}
                        receipt={receipt}
                        sendMessage={sendMessage}
                        sources={enrichedMessage.jsonContent.sources}
                        sourceTitle={translated('Components.Bubble.sourceTitle')}
                        textOnly={true}
                        content={enrichedMessage.jsonContent.response as string}
                    />
                );
                break;
            case JsonContentTypeEnumSchema.enum['application/chat.message.vqa.response']:
                baseComponent = (
                    <Bubble
                        ariaLabel={translated('Components.Bubble.ariaLabel')}
                        key={message.messageId}
                        customer={message.participantRole === MessageParticipantRole.CUSTOMER}
                        isActionable={isActionable}
                        selectedOption={selectedOption}
                        receipt={receipt}
                        sendMessage={sendMessage}
                        events={enrichedMessage.jsonContent.events}
                        eventsTitle={translated('Components.Bubble.eventsTitle')}
                        textOnly={true}
                        content={enrichedMessage.jsonContent.response as string}
                    />
                );
                break;
            case JsonContentTypeEnumSchema.enum['application/sherlock.event.feedback.requested']:
                {
                    const requestMessageId = enrichedMessage.messageId;
                    const feedbackResponse = feedbackMap[requestMessageId];
                    const feedbackResponseAttributes = {
                        ...enrichedMessage.jsonContent.attributes,
                        requestMessageId: enrichedMessage.messageId,
                    };

                    return (
                        <FeedbackCard
                            ariaLabelThumbsDown={translated('Components.FeedbackCard.ariaLabelThumbsDown')}
                            ariaLabelThumbsUp={translated('Components.FeedbackCard.ariaLabelThumbsUp')}
                            disabled={!!feedbackResponse || !isActionable}
                            header={
                                feedbackResponse
                                    ? translated('Components.FeedbackCard.sent')
                                    : translated('Components.FeedbackCard.header')
                            }
                            thumbsUpClick={() => {
                                sendFeedback(IntentFeedback.THUMBS_UP, feedbackResponseAttributes);
                            }}
                            onThumbsDownResponse={(selectedReason) => {
                                const response = {
                                    ...feedbackResponseAttributes,
                                    reason: selectedReason,
                                };
                                sendFeedback(IntentFeedback.THUMBS_DOWN, response);
                            }}
                            rating={feedbackResponse?.rating}
                            reason={feedbackResponse?.reason}
                            thumbsUpText={translated('Components.FeedbackCard.thumbsUp')}
                            thumbsDownText={translated('Components.FeedbackCard.thumbsDown')}
                            thumbsDownResponse={translated('Components.FeedbackCard.thumbsDownResponse')}
                            RESPONSE_UNCLEAR={translated('Components.FeedbackCard.RESPONSE_UNCLEAR')}
                            ISSUE_UNRESOLVED={translated('Components.FeedbackCard.ISSUE_UNRESOLVED')}
                            NOT_HELPFUL={translated('Components.FeedbackCard.NOT_HELPFUL')}
                        />
                    );
                }
            default:
            // No additional action required if jsonContent type does not match above jsonContent types
        }

        return baseComponent;
    };

    return (
        <SessionContainer ref={sessionContainer}>
            <SessionProvider value={sessionContext}>
                <ChatHeader
                    endChat={endChat}
                    isMuted={isMuted}
                    sendSurvey={handleSendSurvey}
                    showSurvey={showSurvey}
                    toggleMute={toggleMute}
                    onToggleWidget={onToggleWidget}
                    menuPortalElement={sessionContainer.current || undefined}
                />
                <Body
                    latestMessageText={translated('Components.Body.latestMessage')}
                    loadMessagesText={translated('Components.Body.loadMessages')}
                    newMessageText={translated('Components.Body.newMessage')}
                    loading={initialLoading}
                    loadPreviousMessages={loadPreviousMessages}
                    onNewMessage={subscribeToNewMessage}
                >
                    {groupMessagesByRole.map((messageGroupOrEvent, index) => (
                        'messages' in messageGroupOrEvent ? (
                            <Entry
                                key={index}
                                participantRole={messageGroupOrEvent.participantRole}
                                timestamp={messageGroupOrEvent.timestamp}
                            >
                                {messageGroupOrEvent.messages.map(message => (
                                    <React.Fragment key={message.messageId}>
                                        {componentConversion(message)}
                                    </React.Fragment>
                                ))}
                            </Entry>
                        ) : (
                            <React.Fragment key={messageGroupOrEvent.messageId}>
                                {componentConversion(messageGroupOrEvent)}
                            </React.Fragment>
                        )
                    ))}
                    {sendingCustomerMessage && (
                        <Entry
                            participantRole={MessageParticipantRole.CUSTOMER}
                            timestamp={Date.now()}
                            loading={true}
                        >
                            <Bubble
                                ariaLabel={translated('Components.Bubble.ariaLabel')}
                                key={'pending-message'}
                                customer={true}
                                sendMessage={sendMessage}
                                isActionable={false}
                                content={sendingCustomerMessage}
                                textOnly={true}
                            />
                        </Entry>
                    )}
                    {typingRole && (
                        <TypingIndicator indicator={typingRole} />
                    )}
                    {pendingMessage && (
                        <Entry
                            participantRole={MessageParticipantRole.CUSTOM_BOT}
                            reasoningText={pendingMessage.reasoning || translated('Components.Entry.reasonings.thinkingText')}
                        >
                            <Bubble
                                ariaLabel={translated('Components.Bubble.ariaLabel')}
                                key={'pending-message'}
                                customer={false}
                                sendMessage={sendMessage}
                                isActionable={false}
                                content={pendingMessage.content}
                                reasoning={!!pendingMessage.reasoning}
                                textOnly={true}
                            />
                        </Entry>
                    )}
                    {messageError && (
                        <Alert
                            ariaLabel={translated('Components.Alert.ariaLabel')}
                            banner={true}
                            closable={true}
                            message={messageError}
                            color='negative'
                            position='topBody'
                            messageLabel={translated('Components.Alert.messageLabel')}
                        />
                    )}
                    {endChatError && (
                        <Alert
                            ariaLabel={translated('Components.Alert.ariaLabel')}
                            banner={true}
                            closable={true}
                            message={translated('Components.Alert.endChatError')}
                            color='negative'
                            position='topBody'
                        />
                    )}
                </Body>
                <Footer
                    ariaLabel={translated('Components.Footer.ariaLabel')}
                    endChat={endChat}
                    loading={loading && !initialLoading}
                    disabled={!!pendingMessage}
                    onSubmit={sendMessage}
                    placeholder={translated('Components.Footer.placeholder')}
                    reasoning={!!pendingMessage}
                    // Only trigger typing if the agent is present
                    onTyping={() => {
                        if (currentParticipantRoles.has(MessageParticipantRole.AGENT)) {
                            session.triggerTyping();
                        }
                    }}
                    windowType={windowType}
                />
            </SessionProvider>
        </SessionContainer>
    );
};
